<script setup lang="ts">
// Profile-generation monitor: the pending in-design profiles + pending persolog-online participants,
// polled every 5s, with regenerate/recalculate/delete actions. Route-gated (requiresAuth), and linked
// from both the Support and Dev sections. Moved out of the former HomePage during the admin restructure.
import { computed, onBeforeUnmount, onMounted, type Ref, ref } from "vue";
import { keycloak } from "../main.js";
import { questionnaireUrl } from "../utils/supportLinks.ts";
import { ProfileAdministrationAPI, ProfileContractorApi } from "../api";
import PendingProfiles from "../components/profiles/PendingProfiles.vue";
import ProfileSkeleton from "../components/profiles/ProfileSkeleton.vue";
import { useToast } from "primevue/usetoast";
import StatusIcon from "../components/participants/StatusIcon.vue";
import { useI18n } from "vue-i18n";
import { PendingParticipant, Profile, Trainer } from "../types/PersologOnlineTypes.ts";

const { t } = useI18n();
const authenticated = computed(() => keycloak.authenticated || false);

let intervalId: any;
const toast = useToast();
const expandedRows = ref<PendingParticipant[]>([]);
const pendingProfilesInDesign = ref([]) as Ref<Profile[]>;
const pendingProfilesPersologOnline = ref([]) as Ref<PendingParticipant[]>;
const unrelatedProfiles = ref([]) as Ref<Profile[]>;
const loading = ref(false);
const trainer = ref<Trainer>();
const isFetching = ref(false);
const groupCharts = ref<string[]>([]);

const startInterval = () => {
    intervalId = setInterval(() => {
        // No point polling every 5s into a tab nobody is looking at.
        if (!globalThis.document?.hidden) {
            fetchPendingProfiles();
        }
    }, 5000);
};

// Catch up immediately when the tab comes back, rather than showing stale rows for up to 5s.
const onVisibilityChange = () => {
    if (!globalThis.document.hidden) {
        fetchPendingProfiles();
    }
};

async function triggerFetching() {
    loading.value = true;
    await fetchPendingProfiles();
    loading.value = false;
}

const fetchPendingProfiles = async () => {
    if (isFetching.value) return;
    isFetching.value = true;

    try {
        const currentExpandedIds = new Set<number>(
            expandedRows.value
                .map((row) => row.id)
                .filter((id): id is number => id !== undefined)
        );

        const rawProfiles = await ProfileContractorApi.fetchPendingProfiles();
        pendingProfilesInDesign.value = rawProfiles.sort((a, b) => a.id - b.id);

        const participants = await ProfileAdministrationAPI.fetchPendingParticipants();
        const updated = participants
            .filter((p): p is PendingParticipant & { id: number } => p.id !== undefined)
            .sort((a, b) => a.id - b.id);

        pendingProfilesPersologOnline.value = updated;

        expandedRows.value = updated.filter((p) => currentExpandedIds.has(p.id));

        unrelatedProfiles.value = getUnrelatedProfiles();
    } catch (error) {
        console.error("Error fetching pending profiles:", error);
        showError(t("messages.load.error"));
    } finally {
        isFetching.value = false;
    }
};

const showError = (message: string) => {
    toast.add({
        severity: "error",
        summary: t("messages.error"),
        detail: message,
        life: 3000,
    });
};

const showSuccess = (message: string) => {
    toast.add({
        severity: "success",
        summary: t("messages.success"),
        detail: message,
        life: 3000,
    });
};

const regenerateProfile = async (profile: Profile, deleteFirst: boolean = true) => {
    try {
        if (deleteFirst) {
            await deleteProfile(profile);
        }
        if (groupCharts.value.includes(profile.product)) {
            await ProfileContractorApi.regenerateGroupChart(profile);
        } else {
            await ProfileContractorApi.regenerateProfile(profile);
        }
        showSuccess(t("messages.regenerate.success", { profile: profile.id }));
        await fetchPendingProfiles();
    } catch (e) {
        console.error("Error during regenerateProfile:", e);
        showError(t("messages.regenerate.error", { profile: profile.id }));
    }
};

const recalculateProfile = async (profile: Profile) => {
    try {
        await ProfileContractorApi.recalculateProfile(profile);
        await ProfileContractorApi.regenerateProfile(profile);
        showSuccess(t("messages.recalculate.success", { profile: profile.id }));
        await fetchPendingProfiles();
    } catch (e) {
        console.error("Error during recalculateProfile:", e);
        showError(t("messages.recalculate.error", { profile: profile.id }));
    }
};

const deleteProfile = async (profile: Profile) => {
    try {
        await ProfileContractorApi.deleteProfile(profile);
        await fetchPendingProfiles();
    } catch (e) {
        console.error("Error during deleteProfile:", e);
        showError(t("messages.delete.error", { profile: profile.id }));
    }
};

function getPendingProfiles(participantId: number, projectId: number) {
    return pendingProfilesInDesign.value.filter((p) => p.id === participantId || p.id === projectId);
}

function getUnrelatedProfiles() {
    const ids = new Set(pendingProfilesPersologOnline.value.map((p) => p.id));
    return pendingProfilesInDesign.value.filter((p) => !ids.has(p.id));
}

async function getTrainerName(id: string) {
    trainer.value = await ProfileAdministrationAPI.fetchTrainer(id);
}

function toProfile(participant: PendingParticipant): Profile {
    return {
        id: participant.id ?? 0,
        hash: participant.questionnaire ?? '',
        product: participant.selectedProduct,
        language: participant.project.language,
    };
}

const onRowExpand = (event: any) => {
    getTrainerName(event.data.project.trainer);
};

onMounted(async () => {
    if (authenticated.value) {
        await triggerFetching();
        startInterval();
        globalThis.document.addEventListener("visibilitychange", onVisibilityChange);
        groupCharts.value = await ProfileAdministrationAPI.getGroupCharts()
    }
});

onBeforeUnmount(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
    globalThis.document.removeEventListener("visibilitychange", onVisibilityChange);
});
</script>

<template>
    <div>
        <h1 class="page-title">{{ t("profileGeneration.title") }}</h1>
        <div id="pendingProfilesTable" class="my-8">
            <div class="flex justify-end mb-4">
                <Button rounded icon="pi pi-refresh" @click="triggerFetching" />
            </div>
            <ProfileSkeleton v-if="loading" />
            <PendingProfiles
                v-else
                :key="pendingProfilesInDesign.length"
                :profiles="pendingProfilesInDesign"
                :group-charts="groupCharts"
                @recalculate="recalculateProfile"
                @regenerate="regenerateProfile"
                @delete="deleteProfile"
            />
        </div>
        <div id="pendingParticipantsTable" class="my-8">
            <ProfileSkeleton v-if="loading" />
            <DataTable v-else v-model:expanded-rows="expandedRows" :value="pendingProfilesPersologOnline" data-key="id" @rowExpand="onRowExpand">
                <template #header>
                    <div class="flex flex-wrap items-center justify-between">
                        <span class="text-xl text-content font-bold">{{ t("pending.participants.header") }}</span>
                    </div>
                </template>
                <Column expander style="width: 5rem" />
                <Column field="firstName" :header="t('pending.participants.firstname')"></Column>
                <Column field="name" :header="t('pending.participants.lastname')"></Column>
                <Column field="email" :header="t('pending.participants.email')">
                    <template #body="slotProps">
                        <router-link :to="{ path: '/participants', query: { email: slotProps.data.email } }">
                            {{ slotProps.data.email }}
                        </router-link>
                    </template>
                </Column>
                <Column field="selectedProduct" :header="t('pending.participants.product')">
                    <template #body="slotProps">
                        <Tag style="font-weight: normal" severity="info" :value="slotProps.data.selectedProduct.toUpperCase()" />
                    </template>
                </Column>
                <Column field="project.language" :header="t('projects.language')">
                    <template #body="slotProps">
                        <Tag style="font-weight: normal" severity="secondary" :value="slotProps.data.project.language.toUpperCase()" />
                    </template>
                </Column>
                <Column field="status" :header="t('pending.participants.status')">
                    <template #body="slotProps">
                        <StatusIcon :status="slotProps.data.status"></StatusIcon>
                    </template>
                </Column>
                <Column field="actions" :header="t('pending.participants.actions')">
                    <template #body="rowData">
                        <Button
                            icon="pi pi-replay"
                            rounded
                            severity="secondary"
                            class="mr-2"
                            size="small"
                            @click="regenerateProfile(toProfile(rowData.data), false)"
                        />
                        <Button
                            icon="pi pi-calculator"
                            rounded
                            severity="secondary"
                            class="mr-2"
                            size="small"
                            @click="recalculateProfile(toProfile(rowData.data))"
                        />
                    </template>
                </Column>
                <template #expansion="slotProps">
                    <div>{{ t("pending.participants.id") }}: {{ slotProps.data.id }}</div>
                    <div>
                        {{ t("projects.label") }} ({{ t("projects.id") }}):
                        {{ slotProps.data.project.name }} ({{ slotProps.data.project.id }})
                        <router-link :to="{ path: '/projects', query: { id: slotProps.data.project.trainer } }">{{ t("support.links.showProjects") }}</router-link>
                    </div>
                    <div>
                        {{ t("projects.trainer") }}: {{ trainer?.firstname }} {{ trainer?.lastname }}
                        <router-link v-if="trainer" :to="{ path: '/trainers', query: { email: trainer.email } }">{{ t("support.links.showTrainer") }}</router-link>
                    </div>
                    <div>
                        {{ t("projects.questionnaire") }}:
                        <a v-if="questionnaireUrl(slotProps.data.questionnaire)" :href="questionnaireUrl(slotProps.data.questionnaire)" target="_blank">{{
                            slotProps.data.questionnaire
                        }}</a>
                        <span v-else>{{ slotProps.data.questionnaire }}</span>
                    </div>
                    <PendingProfiles
                        :key="slotProps.data.id + '-' + pendingProfilesInDesign.length"
                        :profiles="getPendingProfiles(slotProps.data.id, slotProps.data.project.id)"
                        :group-charts="groupCharts"
                        class="my-4"
                        @recalculate="recalculateProfile"
                        @regenerate="regenerateProfile"
                        @delete="deleteProfile"
                    />
                </template>
            </DataTable>
        </div>
    </div>
</template>
